import React, { useState } from 'react';
import { Publication } from '../../../types/profile';
import { EditableSection } from '../common/EditableSection';
import { FormInput } from '../common/FormInput';
import { FormSelect } from '../common/FormSelect';
import { FormTextArea } from '../common/FormTextArea';

interface PublicationsSectionProps {
  publications: Publication[];
  onUpdate: (publications: Publication[]) => void;
}

interface PublicationFormProps {
  publication: Publication;
  onChange: (publication: Publication) => void;
  onRemove: () => void;
}

const PublicationForm: React.FC<PublicationFormProps> = ({ publication, onChange, onRemove }) => {
  const handleFieldChange = (field: keyof Publication) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    onChange({ ...publication, [field]: e.target.value });
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 80; year--) {
      years.push(year.toString());
    }
    return years;
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-4">
      <div className="flex justify-between items-start">
        <h4 className="text-lg font-medium text-gray-900">Publication Entry</h4>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-600 hover:text-red-800"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
      <FormInput
        label="Publication Title"
        name="publication_title"
        value={publication.publication_title}
        onChange={handleFieldChange('publication_title')}
        maxLength={100}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormSelect
          label="Publication Date"
          name="publication_date"
          value={publication.publication_date}
          onChange={handleFieldChange('publication_date')}
          options={generateYearOptions()}
          placeholder="Select year"
        />
        <FormInput
          label="Publication Organization"
          name="publication_organization"
          value={publication.publication_organization}
          onChange={handleFieldChange('publication_organization')}
          maxLength={100}
        />
      </div>
      <FormTextArea
        label="Description"
        name="publication_description"
        value={publication.publication_description}
        onChange={handleFieldChange('publication_description')}
        maxLength={100}
        rows={3}
      />
    </div>
  );
};

export const PublicationsSection: React.FC<PublicationsSectionProps> = ({
  publications,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editPublications, setEditPublications] = useState<Publication[]>(publications);

  const handleEdit = () => {
    setEditPublications(publications);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditPublications(publications);
    setIsEditing(false);
  };

  const handleSave = () => {
    const sortedPublications = [...editPublications].sort((a, b) => {
      const dateA = a.publication_date || '0000';
      const dateB = b.publication_date || '0000';
      return dateB.localeCompare(dateA);
    });
    onUpdate(sortedPublications);
    setIsEditing(false);
  };

  const handleAddPublication = () => {
    setEditPublications([
      ...editPublications,
      {
        publication_title: '',
        publication_date: '',
        publication_organization: '',
        publication_description: ''
      }
    ]);
  };

  const handleUpdatePublication = (index: number) => (pub: Publication) => {
    const updated = [...editPublications];
    updated[index] = pub;
    setEditPublications(updated);
  };

  const handleRemovePublication = (index: number) => () => {
    setEditPublications(editPublications.filter((_, i) => i !== index));
  };

  const sortedViewPublications = [...publications].sort((a, b) => {
    const dateA = a.publication_date || '0000';
    const dateB = b.publication_date || '0000';
    return dateB.localeCompare(dateA);
  });

  return (
    <EditableSection
      title="Publications"
      isEditing={isEditing}
      onEdit={handleEdit}
      onSave={handleSave}
      onCancel={handleCancel}
    >
      {isEditing ? (
        <div className="space-y-4">
          {editPublications.map((pub, index) => (
            <PublicationForm
              key={index}
              publication={pub}
              onChange={handleUpdatePublication(index)}
              onRemove={handleRemovePublication(index)}
            />
          ))}
          <button
            type="button"
            onClick={handleAddPublication}
            className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-md text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            + Add Publication
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedViewPublications.length > 0 ? (
            sortedViewPublications.map((pub, index) => (
              <div key={index} className="border-b border-gray-200 pb-4 last:border-0">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{pub.publication_title}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {pub.publication_organization}
                    </p>
                    {pub.publication_description && (
                      <p className="text-sm text-gray-600 mt-2">{pub.publication_description}</p>
                    )}
                  </div>
                  {pub.publication_date && (
                    <span className="text-sm text-gray-500">{pub.publication_date}</span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 italic">No publications added yet.</p>
          )}
        </div>
      )}
    </EditableSection>
  );
};